/**
 * Unify Group therapy FHIR model.
 *
 * Therapy cohorts use FHIR `Group` as the roster/capacity/schedule shell.
 * Clinical records remain Patient/EpisodeOfCare scoped — never on the Group.
 *
 * Distinguished from messaging Groups via `meta.tag` (GROUP_THERAPY_TAG_SYSTEM).
 */
import type { Extension, Group, Reference } from '@medplum/fhirtypes';

import {
  GROUP_CAPACITY_EXT,
  GROUP_DESCRIPTION_EXT,
  GROUP_ENROLLMENT_LOCK_DATE_EXT,
  GROUP_ENROLLMENT_MODE_EXT,
  GROUP_FACILITATOR_EXT,
  GROUP_KIND_EXT,
  GROUP_LOCATION_EXT,
  GROUP_MAX_CAPACITY_EXT,
  GROUP_MEMBER_ROLE_EXT,
  GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE,
  GROUP_MODALITY_EXT,
  GROUP_PRIMARY_FACILITATOR_EXT,
  GROUP_ROOM_EXT,
  GROUP_DEVICE_EXT,
  GROUP_SCHEDULE_EXT,
  GROUP_TEMPLATE_EXT,
  GROUP_LIFECYCLE_INACTIVE_CODE,
  GROUP_LIFECYCLE_TAG_SYSTEM,
  GROUP_THERAPY_TAG_CODE,
  GROUP_THERAPY_TAG_SYSTEM,
  SESSION_MODALITY_SYSTEM,
  THERAPY_SERVICE_TYPE_SYSTEM,
} from '../constants';

/** Enrollment mode for therapy cohorts (PRD: closed | open only). */
export type GroupEnrollmentMode = 'closed' | 'open';

/** Session modality codes used on Group.extension[group-modality]. */
export type GroupSessionModality = 'in-person' | 'telehealth' | 'hybrid' | 'either';

/** Recurrence pattern on Group.extension[group-schedule].extension[recurrence]. */
export type GroupScheduleRecurrence = 'daily' | 'weekly' | 'biweekly' | 'monthly' | 'custom';

export type GroupScheduleData = {
  startDate?: string;
  endDate?: string;
  startTime?: string;
  endTime?: string;
  sessionCount?: number;
  daysOfWeek: string[];
  timezone?: string;
  /** Canonical recurrence; biweekly means every other week. */
  recurrence?: GroupScheduleRecurrence;
  /** Explicit session dates (YYYY-MM-DD) when recurrence === 'custom'. */
  sessionDates?: string[];
};

export function resolveGroupScheduleRecurrence(
  schedule: Pick<GroupScheduleData, 'recurrence'>,
): GroupScheduleRecurrence {
  return schedule.recurrence ?? 'weekly';
}

export type GroupCapacityData = {
  minEnrollment?: number;
  maxEnrollment?: number;
  minSessionsToAttend?: number;
};

type GroupMember = NonNullable<Group['member']>[number];

// ── Tag / identification ───────────────────────────────────────────────────

export function isTherapyCohortGroup(resource: Group): boolean {
  return (
    resource.meta?.tag?.some(
      (t) => t.system === GROUP_THERAPY_TAG_SYSTEM && t.code === GROUP_THERAPY_TAG_CODE,
    ) ?? false
  );
}

export function withTherapyCohortTag(resource: Group): Group {
  const existing =
    resource.meta?.tag?.filter(
      (t) => !(t.system === GROUP_THERAPY_TAG_SYSTEM && t.code === GROUP_THERAPY_TAG_CODE),
    ) ?? [];
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [...existing, { system: GROUP_THERAPY_TAG_SYSTEM, code: GROUP_THERAPY_TAG_CODE }],
    },
  };
}

/**
 * @deprecated Lifecycle is `Group.active` only. Kept for reading legacy archived tags.
 * Prefer `resource.active === false` for inactive cohorts.
 */
export function isGroupLifecycleInactive(resource: Group): boolean {
  return (
    resource.meta?.tag?.some(
      (t) => t.system === GROUP_LIFECYCLE_TAG_SYSTEM && t.code === GROUP_LIFECYCLE_INACTIVE_CODE,
    ) ?? false
  );
}

/** @deprecated Do not write; end cohorts with `active = false` only. */
export function withGroupLifecycleInactiveTag(resource: Group): Group {
  const existing =
    resource.meta?.tag?.filter(
      (t) =>
        !(t.system === GROUP_LIFECYCLE_TAG_SYSTEM && t.code === GROUP_LIFECYCLE_INACTIVE_CODE),
    ) ?? [];
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [
        ...existing,
        {
          system: GROUP_LIFECYCLE_TAG_SYSTEM,
          code: GROUP_LIFECYCLE_INACTIVE_CODE,
          display: 'Inactive',
        },
      ],
    },
  };
}

/** Strips legacy inactive lifecycle tags when normalizing Groups. */
export function clearGroupLifecycleInactiveTag(resource: Group): Group {
  const existing =
    resource.meta?.tag?.filter(
      (t) =>
        !(t.system === GROUP_LIFECYCLE_TAG_SYSTEM && t.code === GROUP_LIFECYCLE_INACTIVE_CODE),
    ) ?? [];
  if (existing.length === (resource.meta?.tag?.length ?? 0)) return resource;
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: existing.length ? existing : undefined,
    },
  };
}

// ── Extension getters ────────────────────────────────────────────────────────

function subExt(extensions: Extension[] | undefined, url: string): Extension | undefined {
  return extensions?.find((e) => e.url === url);
}

function memberHasRole(member: GroupMember, role: string): boolean {
  return (
    member.extension?.some(
      (extension) => extension.url === GROUP_MEMBER_ROLE_EXT && extension.valueCode === role,
    ) ?? false
  );
}

function withMemberRole(member: GroupMember, role: string, enabled: boolean): GroupMember {
  const extensions = (member.extension ?? []).filter(
    (extension) => !(extension.url === GROUP_MEMBER_ROLE_EXT && extension.valueCode === role),
  );
  if (enabled) {
    extensions.push({ url: GROUP_MEMBER_ROLE_EXT, valueCode: role });
  }
  return { ...member, extension: extensions.length ? extensions : undefined };
}

function activePractitionerMembers(resource: Group): GroupMember[] {
  return (resource.member ?? []).filter(
    (member) => !member.inactive && member.entity.reference?.startsWith('Practitioner/'),
  );
}

function withoutResourceExtensions(resource: Group, urls: Set<string>): Group {
  const extension = (resource.extension ?? []).filter((item) => !urls.has(item.url));
  return { ...resource, extension: extension.length ? extension : undefined };
}

export function getGroupKind(resource: Group): string | undefined {
  const fromCode = resource.code?.coding?.find((c) => c.system === THERAPY_SERVICE_TYPE_SYSTEM)?.code;
  if (fromCode) return fromCode;
  return resource.extension?.find((e) => e.url === GROUP_KIND_EXT)?.valueCode;
}

export function getGroupEnrollmentMode(resource: Group): GroupEnrollmentMode | string | undefined {
  return resource.extension?.find((e) => e.url === GROUP_ENROLLMENT_MODE_EXT)?.valueCode;
}

export function getGroupPrimaryFacilitator(resource: Group): Reference | undefined {
  const member = activePractitionerMembers(resource).find((item) =>
    memberHasRole(item, GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE),
  );
  return (
    member?.entity ??
    resource.extension?.find((e) => e.url === GROUP_PRIMARY_FACILITATOR_EXT)?.valueReference
  );
}

export function getGroupFacilitators(resource: Group): Reference[] {
  const practitionerMembers = activePractitionerMembers(resource);
  if (practitionerMembers.length) {
    const primary = practitionerMembers.find((member) =>
      memberHasRole(member, GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE),
    );
    const additional = practitionerMembers.filter((member) => member !== primary);
    return [...(primary ? [primary.entity] : []), ...additional.map((member) => member.entity)];
  }

  const primary = getGroupPrimaryFacilitator(resource);
  const fromExt =
    resource.extension
      ?.filter((e) => e.url === GROUP_FACILITATOR_EXT)
      .map((e) => e.valueReference)
      .filter((r): r is Reference => !!r?.reference) ?? [];
  if (primary?.reference) {
    const rest = fromExt.filter((r) => r.reference !== primary.reference);
    return [primary, ...rest];
  }
  return fromExt;
}

export function getGroupCapacity(resource: Group): GroupCapacityData {
  const maxFromNew = resource.extension?.find((e) => e.url === GROUP_MAX_CAPACITY_EXT)
    ?.valueUnsignedInt;
  const cap = resource.extension?.find((e) => e.url === GROUP_CAPACITY_EXT);
  return {
    minEnrollment: subExt(cap?.extension, 'minEnrollment')?.valueInteger,
    maxEnrollment: maxFromNew ?? subExt(cap?.extension, 'maxEnrollment')?.valueInteger,
    minSessionsToAttend: subExt(cap?.extension, 'minSessionsToAttend')?.valueInteger,
  };
}

export function getGroupSchedule(resource: Group): GroupScheduleData {
  const sched = resource.extension?.find((e) => e.url === GROUP_SCHEDULE_EXT);
  const subs = sched?.extension ?? [];
  const startDate = subExt(subs, 'start-date')?.valueDate;
  const endDate = subExt(subs, 'end-date')?.valueDate;
  const startTimeRaw =
    subExt(subs, 'start-time')?.valueTime ?? subExt(subs, 'start-time')?.valueString;
  const endTimeRaw =
    subExt(subs, 'end-time')?.valueTime ?? subExt(subs, 'end-time')?.valueString;
  const startTime = startTimeRaw?.slice(0, 5);
  const endTime = endTimeRaw?.slice(0, 5);
  const daysOfWeek = (subs ?? [])
    .filter((e) => e.url === 'day-of-week')
    .map((e) => e.valueCode ?? e.valueString)
    .filter((v): v is string => typeof v === 'string');
  const timezone =
    subExt(subs, 'timezone')?.valueCode ?? subExt(subs, 'timezone')?.valueString;
  const recurrenceRaw =
    subExt(subs, 'recurrence')?.valueCode ?? subExt(subs, 'recurrence')?.valueString;
  const recurrence =
    recurrenceRaw === 'daily' ||
    recurrenceRaw === 'weekly' ||
    recurrenceRaw === 'biweekly' ||
    recurrenceRaw === 'monthly' ||
    recurrenceRaw === 'custom'
      ? recurrenceRaw
      : undefined;
  const sessionDates = [
    ...subs
      .filter((e) => e.url === 'session-date')
      .map((e) => e.valueDate)
      .filter((v): v is string => typeof v === 'string' && !!v.trim()),
  ];
  return {
    startDate,
    endDate,
    startTime,
    endTime,
    sessionCount: subExt(subs, 'session-count')?.valueInteger,
    daysOfWeek: [...new Set(daysOfWeek)],
    timezone,
    recurrence,
    sessionDates: sessionDates.length ? [...new Set(sessionDates)].sort() : undefined,
  };
}

/** Group Template HealthcareService reference (canonical for new cohorts). */
export function getGroupTemplateRef(resource: Group): Reference | undefined {
  return resource.extension?.find((e) => e.url === GROUP_TEMPLATE_EXT)?.valueReference;
}

export function setGroupTemplateRef(resource: Group, ref: Reference | undefined): Group {
  return replaceExtension(
    resource,
    GROUP_TEMPLATE_EXT,
    ref ? { url: GROUP_TEMPLATE_EXT, valueReference: ref } : undefined,
  );
}

export function getGroupModality(resource: Group): GroupSessionModality | undefined {
  const code =
    resource.extension?.find((e) => e.url === GROUP_MODALITY_EXT)?.valueCoding?.code ??
    resource.extension?.find((e) => e.url === GROUP_MODALITY_EXT)?.valueCode;
  if (
    code === 'in-person' ||
    code === 'telehealth' ||
    code === 'hybrid' ||
    code === 'either'
  ) {
    return code;
  }
  return undefined;
}

export function getGroupLocation(resource: Group): Reference | undefined {
  return resource.extension?.find((e) => e.url === GROUP_LOCATION_EXT)?.valueReference;
}

export function getGroupRoom(resource: Group): Reference | undefined {
  return resource.extension?.find((e) => e.url === GROUP_ROOM_EXT)?.valueReference;
}

export function getGroupDevices(resource: Group): Reference[] {
  return (resource.extension ?? [])
    .filter((e) => e.url === GROUP_DEVICE_EXT && e.valueReference?.reference)
    .map((e) => e.valueReference!);
}

/** Active Patient members only (excludes Practitioners). */
export function getActiveGroupPatientMemberCount(resource: Group): number {
  return (resource.member ?? []).filter(
    (m) => !m.inactive && m.entity?.reference?.startsWith('Patient/'),
  ).length;
}

export function getGroupDescription(resource: Group): string | undefined {
  const ext = resource.extension?.find((e) => e.url === GROUP_DESCRIPTION_EXT);
  return ext?.valueMarkdown ?? ext?.valueString;
}

export function getGroupEnrollmentLockDate(resource: Group): string | undefined {
  return resource.extension?.find((e) => e.url === GROUP_ENROLLMENT_LOCK_DATE_EXT)?.valueDate;
}

export function getManagingOrganizationId(resource: Group): string | undefined {
  const ref = resource.managingEntity?.reference;
  return ref?.startsWith('Organization/') ? ref.slice('Organization/'.length) : undefined;
}

// ── Extension setters (immutable copies) ─────────────────────────────────────

function replaceExtension(resource: Group, url: string, next: Extension | undefined): Group {
  const others = (resource.extension ?? []).filter((e) => e.url !== url);
  const extension = next ? [...others, next] : others;
  return { ...resource, extension: extension.length ? extension : undefined };
}

export function setGroupEnrollmentMode(
  resource: Group,
  mode: GroupEnrollmentMode | string | undefined,
): Group {
  return replaceExtension(
    resource,
    GROUP_ENROLLMENT_MODE_EXT,
    mode ? { url: GROUP_ENROLLMENT_MODE_EXT, valueCode: mode } : undefined,
  );
}

export function setGroupPrimaryFacilitator(
  resource: Group,
  ref: Reference | undefined,
): Group {
  const target = ref?.reference;
  const members = (resource.member ?? []).map((member) =>
    withMemberRole(
      member,
      GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE,
      !!target && !member.inactive && member.entity.reference === target,
    ),
  );
  if (ref?.reference && !members.some((member) => member.entity.reference === ref.reference)) {
    members.push(
      withMemberRole(
        { entity: ref as GroupMember['entity'], inactive: false },
        GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE,
        true,
      ),
    );
  }
  return withoutResourceExtensions(
    { ...resource, member: members.length ? members : undefined },
    new Set([GROUP_PRIMARY_FACILITATOR_EXT]),
  );
}

export function setGroupFacilitators(resource: Group, refs: Reference[]): Group {
  const uniqueRefs = refs.filter(
    (ref, index) =>
      !!ref.reference && refs.findIndex((candidate) => candidate.reference === ref.reference) === index,
  );
  const existingByReference = new Map(
    (resource.member ?? [])
      .filter((member) => member.entity.reference?.startsWith('Practitioner/'))
      .map((member) => [member.entity.reference as string, member]),
  );
  const nonPractitionerMembers = (resource.member ?? []).filter(
    (member) => !member.entity.reference?.startsWith('Practitioner/'),
  );
  const practitionerMembers = uniqueRefs.map((ref, index) => {
    const existing = existingByReference.get(ref.reference as string);
    return withMemberRole(
      {
        ...existing,
        entity: { ...existing?.entity, ...ref } as GroupMember['entity'],
        inactive: false,
      },
      GROUP_MEMBER_ROLE_PRIMARY_FACILITATOR_CODE,
      index === 0,
    );
  });
  return withoutResourceExtensions(
    {
      ...resource,
      member: [...nonPractitionerMembers, ...practitionerMembers],
    },
    new Set([GROUP_FACILITATOR_EXT, GROUP_PRIMARY_FACILITATOR_EXT]),
  );
}

export function getGroupMemberRoles(member: GroupMember): string[] {
  return (member.extension ?? [])
    .filter((extension) => extension.url === GROUP_MEMBER_ROLE_EXT)
    .map((extension) => extension.valueCode)
    .filter((role): role is string => !!role);
}

export function setGroupCapacity(resource: Group, capacity: GroupCapacityData): Group {
  const sub: Extension[] = [];
  if (capacity.minEnrollment != null) {
    sub.push({ url: 'minEnrollment', valueInteger: capacity.minEnrollment });
  }
  if (capacity.maxEnrollment != null) {
    sub.push({ url: 'maxEnrollment', valueInteger: capacity.maxEnrollment });
  }
  if (capacity.minSessionsToAttend != null) {
    sub.push({ url: 'minSessionsToAttend', valueInteger: capacity.minSessionsToAttend });
  }
  let next = replaceExtension(
    resource,
    GROUP_CAPACITY_EXT,
    sub.length ? { url: GROUP_CAPACITY_EXT, extension: sub } : undefined,
  );
  next = replaceExtension(
    next,
    GROUP_MAX_CAPACITY_EXT,
    capacity.maxEnrollment != null
      ? { url: GROUP_MAX_CAPACITY_EXT, valueUnsignedInt: capacity.maxEnrollment }
      : undefined,
  );
  return next;
}

export function setGroupSchedule(resource: Group, schedule: GroupScheduleData): Group {
  const fhirTime = (value: string): string =>
    /^\d{2}:\d{2}$/.test(value) ? `${value}:00` : value;
  const sub: Extension[] = [];
  if (schedule.startDate) sub.push({ url: 'start-date', valueDate: schedule.startDate });
  if (schedule.endDate) sub.push({ url: 'end-date', valueDate: schedule.endDate });
  if (schedule.startTime) sub.push({ url: 'start-time', valueTime: fhirTime(schedule.startTime) });
  if (schedule.endTime) sub.push({ url: 'end-time', valueTime: fhirTime(schedule.endTime) });
  if (schedule.sessionCount != null) {
    sub.push({ url: 'session-count', valueInteger: schedule.sessionCount });
  }
  if (schedule.timezone) {
    sub.push({ url: 'timezone', valueCode: schedule.timezone });
  }
  const recurrence = resolveGroupScheduleRecurrence(schedule);
  sub.push({ url: 'recurrence', valueCode: recurrence });
  if (recurrence === 'weekly' || recurrence === 'biweekly') {
    for (const day of schedule.daysOfWeek) {
      sub.push({ url: 'day-of-week', valueCode: day });
    }
  }
  if (recurrence === 'custom') {
    for (const date of [...new Set(schedule.sessionDates ?? [])].sort()) {
      if (date.trim()) sub.push({ url: 'session-date', valueDate: date.trim() });
    }
  }
  return replaceExtension(
    resource,
    GROUP_SCHEDULE_EXT,
    sub.length ? { url: GROUP_SCHEDULE_EXT, extension: sub } : undefined,
  );
}

export function setGroupModality(
  resource: Group,
  modality: GroupSessionModality | undefined,
): Group {
  return replaceExtension(
    resource,
    GROUP_MODALITY_EXT,
    modality
      ? {
          url: GROUP_MODALITY_EXT,
          valueCoding: { system: SESSION_MODALITY_SYSTEM, code: modality },
        }
      : undefined,
  );
}

export function setGroupLocation(resource: Group, ref: Reference | undefined): Group {
  return replaceExtension(
    resource,
    GROUP_LOCATION_EXT,
    ref ? { url: GROUP_LOCATION_EXT, valueReference: ref } : undefined,
  );
}

export function setGroupRoom(resource: Group, ref: Reference | undefined): Group {
  return replaceExtension(
    resource,
    GROUP_ROOM_EXT,
    ref ? { url: GROUP_ROOM_EXT, valueReference: ref } : undefined,
  );
}

export function setGroupDevices(resource: Group, refs: Reference[]): Group {
  const others = (resource.extension ?? []).filter((e) => e.url !== GROUP_DEVICE_EXT);
  const deviceExts = refs
    .filter((r) => r.reference)
    .map((r) => ({ url: GROUP_DEVICE_EXT, valueReference: r }));
  const extension = [...others, ...deviceExts];
  return { ...resource, extension: extension.length ? extension : undefined };
}

export function setGroupDescription(resource: Group, description: string | undefined): Group {
  return replaceExtension(
    resource,
    GROUP_DESCRIPTION_EXT,
    description?.trim()
      ? { url: GROUP_DESCRIPTION_EXT, valueMarkdown: description.trim() }
      : undefined,
  );
}

export function setGroupEnrollmentLockDate(resource: Group, date: string | undefined): Group {
  return replaceExtension(
    resource,
    GROUP_ENROLLMENT_LOCK_DATE_EXT,
    date ? { url: GROUP_ENROLLMENT_LOCK_DATE_EXT, valueDate: date } : undefined,
  );
}

export function isGroupEnrollmentLocked(resource: Group, asOfDate = new Date()): boolean {
  const lockDate = getGroupEnrollmentLockDate(resource);
  if (!lockDate) return false;
  const mode = getGroupEnrollmentMode(resource) as string | undefined;
  // Enrollment mode may be absent on Groups that only inherited it from the template.
  // Lock date is only written for closed cohorts, so treat missing mode as closed.
  if (mode === 'open' || mode === 'open-rolling' || mode === 'continuous' || mode === 'single-event') {
    return false;
  }
  // Local calendar day via ISO date slice is intentional for lock comparison.
  const asOf = asOfDate.toISOString().slice(0, 10);
  return asOf > lockDate;
}

export function practitionerRef(id: string, display?: string): Reference {
  return { reference: `Practitioner/${id}`, display };
}

export function organizationRef(id: string, display?: string): Reference {
  return { reference: `Organization/${id}`, display };
}

export function groupRef(id: string, display?: string): Reference {
  return { reference: `Group/${id}`, display };
}

export function locationRef(id: string, display?: string): Reference {
  return { reference: `Location/${id}`, display };
}
